import React, { useState } from 'react';
import { Button, Form, message, Modal } from 'antd';
import { CloseXIcon } from 'icons';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { PatternFormat } from 'react-number-format';

import { useAppDispatch, useAppSelector } from 'store';

import { useCheckSms, useSendSms } from 'modules/auth';
import { globalConst, setFinishLoginModal } from 'modules/global';

interface IProps {
  redirectOnFinish?: string;
  onFinishComplete?: () => void;
  requiredFlow?: boolean;
  lastStepButtonText?: string;
}

const { useWatch, useForm } = Form;

export const LoginModal: React.FC<IProps> = ({ requiredFlow = false, lastStepButtonText }) => {
  const [form] = useForm();
  const t = useTranslations();
  const { push } = useRouter();
  const dispatch = useAppDispatch();
  const codeValue = useWatch('code', form);
  const [step, setStep] = useState<1 | 2>(1);
  const user = useAppSelector(state => state.user);
  const { mutate: sendSms, isLoading: sendingSms } = useSendSms();
  const { mutate: checkSms, isLoading: checkingSms } = useCheckSms();
  const open = useAppSelector(({ confirmationModal }) => confirmationModal.finishLoginModalOpen);
  const isRouteLoading = useAppSelector(({ routeLoading }) => routeLoading.isRouteLoading);

  const cleanCode = codeValue?.replace(/[_\s]/g, '');
  const isCodeFilled = step === 2 && cleanCode?.length === 5;

  const onClose = () => {
    if (requiredFlow && step !== 2) return;
    dispatch(setFinishLoginModal(false));
    form.resetFields();
    setStep(1);
  };

  const validatePhone = (phone: string) => {
    const cleaned = phone.replace(/\s|-/g, '');

    return cleaned.length === 13 ? cleaned : null;
  };

  const onStepOne = (phone: string) => {
    sendSms(phone, {
      onSuccess: () => setStep(2)
    });
  };

  const onStepSecond = (phone: string, code: string) => {
    checkSms(
      { phone, code, isSimpleLogin: true },
      {
        onSuccess: () => {
          message.success(t('royxatdan_muvaffaqiyatli_otdingiz'));
          push(globalConst.pageNames.PRO_PAYMENT);
          if (!isRouteLoading) {
            dispatch(setFinishLoginModal(false));
            setStep(1);
          }
        }
      }
    );
  };

  const onFinish = async ({ phone: rawPhone, code }: { phone: string; code: string }) => {
    const phone = validatePhone(rawPhone);

    if (!phone) {
      form.setFields([{ name: 'phone', errors: [t('Togri_telefon_raqam_kiriting')] }]);
      return;
    }

    const actions: Record<typeof step, () => Promise<void> | void> = {
      1: () => onStepOne(phone),
      2: () => onStepSecond(phone, code)
    };

    await actions[step]?.();
  };

  return (
    <Modal
      footer={false}
      open={open}
      centered
      closable={!requiredFlow || step === 2}
      onCancel={onClose}
      afterClose={() => form.resetFields()}
      width="400px"
      closeIcon={
        <div
          className={`flex items-center justify-center rounded-full p-1 ${
            requiredFlow && step !== 2 ? 'cursor-not-allowed bg-gray-400' : 'cursor-pointer bg-gray-800'
          }`}
        >
          <CloseXIcon size="12" />
        </div>
      }
      styles={{
        content: { padding: '16px', borderRadius: '12px' }
      }}
      title={<span className="text-lg">{t('home.banner.royxatdan_otish')}</span>}
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          user_name: user?.name || user?.last_name ? `${user?.name ?? ''} ${user?.last_name ?? ''}`.trim() : undefined,
          phone: user?.phone || '+998'
        }}
        onFinish={onFinish}
      >
        <Form.Item name="phone" label={t('auth.phone_placeholder')} rules={[{ required: true }]}>
          <PatternFormat
            format="+### ## ### ## ##"
            allowEmptyFormatting
            mask=" "
            type="tel"
            disabled={step === 2}
            placeholder={t('auth.phone_placeholder')}
            className={`!h-13 !rounded-xl !border !p-3 hover:!border-green-500 ${
              form.getFieldError('phone')?.length ? '!border-red-500' : '!border-transparent'
            } w-full !bg-gray-100 hover:!outline-green-500 focus:!outline-green-500`}
          />
        </Form.Item>

        {step !== 1 && (
          <div className="flex items-center gap-1">
            <Form.Item
              name="code"
              label={t('auth.otp_validation_message')}
              className="!flex-1"
              rules={[
                { required: true, message: t('auth.otp_validation_message') },
                { len: 5, message: t('auth.otp_validation_message') }
              ]}
            >
              <PatternFormat
                format="#####"
                mask="_"
                allowEmptyFormatting
                placeholder="_ _ _ _ _"
                autoFocus
                className="!h-13 w-full flex-1 !rounded-xl !border-transparent !bg-gray-100 !p-3 hover:!outline-green-500 focus:!outline-green-500"
              />
            </Form.Item>
          </div>
        )}

        <Button
          htmlType="submit"
          disabled={step === 2 && !isCodeFilled}
          loading={sendingSms || checkingSms || isRouteLoading}
          className="!mt-3 !h-11 !w-full !rounded-lg !border-none !bg-green-500 !px-7 !py-5 !text-white hover:!border-none disabled:!bg-gray-200"
        >
          {step === 1 ? t('tasdiqlash_kodini_olish') : lastStepButtonText}
        </Button>
      </Form>
    </Modal>
  );
};
